import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { 
  listCampaignsApi, 
  fetchCampaign, 
  fetchGenesysFilters,
  listScheduledJobsApi,
  createScheduledJobApi,
  deleteScheduledJobApi,
  updateScheduledJobApi
} from '../api/api';

const ScheduledJobs = ({ setAlert, username, userEmail }) => {
  const history = useHistory();
  const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [jobName, setJobName] = useState('');
  const [creatingJob, setCreatingJob] = useState(false);
  const [selectedMinute, setSelectedMinute] = useState('0');
  const [selectedHour, setSelectedHour] = useState('8'); 
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('?'); 
  const [startDaysAgo, setStartDaysAgo] = useState(1); 
  const [endDaysAgo, setEndDaysAgo] = useState(0);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [scorecards, setScorecards] = useState([]);
  const [selectedScorecard, setSelectedScorecard] = useState(null);
  const [loadingScorecards, setLoadingScorecards] = useState(false);
  const [fetchingFilters, setFetchingFilters] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [wrapUpCodeOptions, setWrapUpCodeOptions] = useState([]);
  const [queueOptions, setQueueOptions] = useState([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [wrapupSearch, setWrapupSearch] = useState('');
  const [queueSearch, setQueueSearch] = useState('');
  const [isAgentOpen, setIsAgentOpen] = useState(false);
  const [isWrapupOpen, setIsWrapupOpen] = useState(false);
  const [isQueueOpen, setIsQueueOpen] = useState(false);
  const [divisionIdInput, setDivisionIdInput] = useState('');
  const start = parseInt(startDaysAgo, 10);
  const end = parseInt(endDaysAgo, 10);
  
  const isInvalidRange = start < end;
  const windowSize = parseInt(startDaysAgo, 10) - parseInt(endDaysAgo, 10);
  const frequencyInDays = selectedDayOfWeek === '?' ? 1 : 7;
  
  const hasOverlapRisk = windowSize > frequencyInDays;
  const hasGapRisk = windowSize < frequencyInDays;
  const [editingJobId, setEditingJobId] = useState(null);


  const [filters, setFilters] = useState({
    queueIds: [],
    direction: '',
    userIds: [],
    wrapUpCodes: [],
    ani: '',
    dnis: '',
    divisionIds: []
  });

  useEffect(() => {
    fetchJobs();
    fetchCampaigns();
    loadGenesysFilters();
  }, []);

  useEffect(() => {
    if (!selectedCampaign) {
      setScorecards([]);
      setSelectedScorecard(null);
      return;
    }
    const loadScorecards = async () => {
      setLoadingScorecards(true);
      try {
        const res = await fetchCampaign(selectedCampaign.value);
        const names = res.templates || [];
        setScorecards(names.map((n) => ({ label: n, value: n })));
      } catch (err) {
        console.error('Error loading scorecards:', err);
      } finally {
        setLoadingScorecards(false);
      }
    };
    loadScorecards();
  }, [selectedCampaign]);

  const generateCronExpression = () => {
    const sastHour = parseInt(selectedHour, 10);
    
    // Convert SAST to UTC (-2)
    let utcHour = (sastHour - 2 + 24) % 24;
    
    let cronDow = selectedDayOfWeek;

    // Handle Day Wrap for the Cron Expression
    // If user picks 00:00 or 01:00 SAST, the UTC day is actually the PREVIOUS day
    if (selectedDayOfWeek !== '?') {
      if (sastHour < 2) {
        const currentIndex = DAYS.indexOf(selectedDayOfWeek);
        const prevIndex = (currentIndex - 1 + 7) % 7;
        cronDow = DAYS[prevIndex];
      }
    }

    const dom = cronDow !== '?' ? '?' : '*'; 
    return `cron(${selectedMinute} ${utcHour} ${dom} * ${cronDow} *)`;
  };


  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await listScheduledJobsApi();
      setScheduledJobs(res || []);
    } catch (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to load scheduled jobs.' });
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await listCampaignsApi();
      const items = res.campaigns || [];
      setCampaigns(items.map((c) => ({ label: c.campaignName, value: c.campaignName })));
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadGenesysFilters = async () => {
    setFetchingFilters(true);
    try {
      const res = await fetchGenesysFilters('all');
      if (res.success) {
        setQueueOptions(res.data.queues || []);
        setAgentOptions(res.data.agents || []);
        if (res.data.wrapUpCodes) {
          const mappedWrapup = res.data.wrapUpCodes.map(item => ({
            id: item.id || item.wrapupcodesId,
            name: item.name || item.Name
          }));
          setWrapUpCodeOptions(mappedWrapup);
        }
      }
    } catch (error) {
      setAlert({ variant: 'error', heading: 'Filter Error', text: 'Could not load live Genesys filters.' });
    } finally {
      setFetchingFilters(false);
    }
  };


  const updateFilter = (filterKey, value) => setFilters(prev => ({ ...prev, [filterKey]: value }));
  const removeFromArrayFilter = (filterKey, index) => {
    setFilters(prev => ({ ...prev, [filterKey]: prev[filterKey].filter((_, i) => i !== index) }));
  };
  const addToArrayFilter = (filterKey, value, inputSetter) => {
    if (value.trim()) {
      setFilters(prev => ({ ...prev, [filterKey]: [...prev[filterKey], value.trim()] }));
      inputSetter('');
    }
  };
  const clearAllFilters = () => {
    setFilters({ queueIds: [], direction: '', userIds: [], wrapUpCodes: [], ani: '', dnis: '', divisionIds: [] });
  };


  const handleCreateSchedule = async () => {
    if (!jobName || !selectedCampaign || !selectedScorecard) {
      return setAlert({ variant: 'warning', heading: 'Missing Fields', text: 'Please fill out all required fields.' });
    }

    if (isInvalidRange) {
      return setAlert({ variant: 'error', heading: 'Invalid Range', text: 'Scan "From" must be the same or a higher number than Scan "Until".' });
    }

    if (hasOverlapRisk) {
      const confirm = window.confirm(`Warning: Your scan window (${windowSize} days) is larger than your run frequency (${frequencyInDays} days). This will cause duplicate records. Do you want to continue?`);
      if (!confirm) return;
    }

    if (parseInt(startDaysAgo) < parseInt(endDaysAgo)) {
      return setAlert({ variant: 'warning', heading: 'Invalid Range', text: 'Scan "From" must be a higher number than Scan "Until".' });
    }

    setCreatingJob(true);

    const activeFilters = {};
    if (filters.queueIds.length > 0) activeFilters.queueIds = filters.queueIds;
    if (filters.direction) activeFilters.direction = filters.direction;
    if (filters.userIds.length > 0) activeFilters.userIds = filters.userIds;
    if (filters.wrapUpCodes.length > 0) activeFilters.wrapUpCodes = filters.wrapUpCodes;
    if (filters.ani) activeFilters.ani = filters.ani;
    if (filters.dnis) activeFilters.dnis = filters.dnis;
    if (filters.divisionIds.length > 0) activeFilters.divisionIds = filters.divisionIds;

    const payload = {
      jobName,
      campaign: selectedCampaign.value,
      template: selectedScorecard.value,
      scheduleExpression: generateCronExpression(),
      filters: activeFilters,
      lookback: {
        startDaysAgo: parseInt(startDaysAgo, 10),
        endDaysAgo: parseInt(endDaysAgo, 10)
      },
      createdBy: userEmail
    };

    try {
      if (editingJobId) {
      // UPDATE MODE
        await updateScheduledJobApi(editingJobId, payload);
        setAlert({ variant: 'success', heading: 'Updated', text: 'Schedule updated successfully.' });
      } else {
        await createScheduledJobApi(payload);
        setAlert({ variant: 'success', heading: 'Success', text: 'Scheduled job created successfully.' });
      }
      
      setJobName('');
      setStartDaysAgo(1);
      setEndDaysAgo(0);
      setSelectedDayOfWeek('?');
      setSelectedHour('8');
      setSelectedMinute('0');
      setSelectedCampaign(null);
      setSelectedScorecard(null);
      setEditingJobId(null);
      clearAllFilters();
      fetchJobs();
    } catch (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to save scheduled job.' });
    } finally {
      setCreatingJob(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled job?')) return;
    try {
      console.log('Deleting job with ID:', jobId);
      await deleteScheduledJobApi(jobId);
      setAlert({ variant: 'success', heading: 'Deleted', text: 'Job deleted successfully.' });
      fetchJobs();
    } catch (error) {
      setAlert({ variant: 'error', heading: 'Error', text: 'Failed to delete job.' });
    }
  };

  const formatCron = (cron) => {
    if (!cron) return 'N/A';
    const match = cron.match(/cron\((.*?)\)/);
    if (!match) return cron;

    const [minute, utcHour, dom, month, dow] = match[1].split(' ');
    
    // Convert UTC Hour to SAST (+2)
    let sastHour = (parseInt(utcHour, 10) + 2) % 24;
    
    // Handle Day Wrap for display
    // If SAST hour is smaller than UTC hour, it means we wrapped forward to the next day
    let displayDow = dow;
    if (dow !== '?' && sastHour < parseInt(utcHour, 10)) {
      const currentIndex = DAYS.indexOf(dow);
      displayDow = DAYS[(currentIndex + 1) % 7];
    }

    const time = `${sastHour.toString().padStart(2, '0')}:${minute.padStart(2, '0')} SAST`;
    
    const dayMap = {
      'SUN': 'Sundays', 'MON': 'Mondays', 'TUE': 'Tuesdays', 'WED': 'Wednesdays',
      'THU': 'Thursdays', 'FRI': 'Fridays', 'SAT': 'Saturdays', '?': 'Daily'
    };

    return `${dayMap[displayDow] || 'Daily'} at ${time}`;
  };

  const getDynamicDateLabel = (daysAgo) => {
    const offset = parseInt(daysAgo, 10);
    if (isNaN(offset)) return "...";
    
    const d = new Date();
    d.setDate(d.getDate() - offset);
    
    if (offset === 0) return "Today";
    if (offset === 1) return "Yesterday";

    return d.toLocaleDateString('en-ZA', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleEditClick = (job) => {
    setEditingJobId(job.jobId || job.id);
    setJobName(job.jobName);
    
    // 1. Set Campaign & Scorecard (Updated to use 'template' as per your data structure)
    const campaignObj = campaigns.find(c => c.value === job.campaign);
    setSelectedCampaign(campaignObj || { label: job.campaign, value: job.campaign });
    
    // Important: Use job.template here instead of job.scorecard
    setSelectedScorecard({ label: job.template, value: job.template });

    // 2. Parse Cron back to SAST UI
    const match = job.scheduleExpression.match(/cron\((.*?)\)/);
    if (match) {
      const [minute, utcHour, dom, month, dow] = match[1].split(' ');
      setSelectedMinute(minute);
      setSelectedDayOfWeek(dow);
      // Convert UTC back to SAST (+2)
      const sastHour = (parseInt(utcHour, 10) + 2) % 24;
      setSelectedHour(sastHour.toString());
    }

    // 3. Set Lookback Window
    if (job.lookback) {
      setStartDaysAgo(job.lookback.startDaysAgo.toString());
      setEndDaysAgo(job.lookback.endDaysAgo.toString());
    }

    // 4. Set Filters (FIX: Hydrate with default values to prevent .map() errors)
    setFilters({
      direction: job.filters?.direction || '',
      userIds: job.filters?.userIds || [],
      queueIds: job.filters?.queueIds || [],
      wrapUpCodes: job.filters?.wrapUpCodes || [],
      divisionIds: job.filters?.divisionIds || [],
      ani: job.filters?.ani || '',
      dnis: job.filters?.dnis || ''
    });

    // 5. Scroll to top so user sees the form is populated
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingJobId(null);
    setJobName('');
    setStartDaysAgo(1);
    setEndDaysAgo(0);
    setSelectedDayOfWeek('?');
    setSelectedHour('8');
    setSelectedMinute('0');
    setSelectedCampaign(null);
    setSelectedScorecard(null);
    clearAllFilters();
  };


  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Scheduled Genesys Downloads</h1>
          <p className="text-gray-600 mt-1">Manage automated batch fetches triggered via EventBridge</p>
        </div>
      </div>

      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Schedules</h2>
        {loadingJobs ? (
          <p className="text-sm text-gray-500">Loading schedules...</p>
        ) : scheduledJobs.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No scheduled jobs found. Create one below.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scorecard</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filters</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
              {scheduledJobs.map((job) => (
                <tr key={job.jobId} className="hover:bg-gray-50 transition-colors">
                  {/* COLUMN 1: JOB & TEMPLATE */}
                  <td className="px-4 py-4 align-top">
                    <div className="text-sm font-bold text-gray-900">{job.jobName}</div>
                  </td>

                  <td className="px-4 py-4 align-top text-sm text-gray-600">
                    {job.createdBy}
                  </td>

                  {/* COLUMN 2: CAMPAIGN */}
                  <td className="px-4 py-4 align-top text-sm text-gray-600">
                    {job.campaign}
                  </td>

                  <td className="px-4 py-4 align-top text-sm text-gray-600">
                    {job.template}
                  </td>

                  {/* COLUMN 3: SCHEDULE & LOOKBACK */}
                  <td className="px-4 py-4 align-top">
                    <div className="text-sm font-medium text-gray-900">
                      {formatCron(job.scheduleExpression)}
                    </div>
                    <div className="text-xs text-amber-600 mt-1">
                      Window: {job.lookback?.startDaysAgo} to {job.lookback?.endDaysAgo} days ago
                    </div>
                  </td>

                  {/* COLUMN 4: ACTIVE FILTERS */}
                  <td className="px-4 py-4 align-top">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {/* Direction Badge */}
                      {job.filters?.direction && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded border border-gray-200">
                          {job.filters.direction.toUpperCase()}
                        </span>
                      )}

                      {/* Queues Badge */}
                      {job.filters?.queueIds?.length > 0 && (
                        <span className="px-2 py-0.5 bg-cyan-50 text-cyan-700 text-[10px] rounded border border-cyan-100">
                          {job.filters.queueIds.length} Queues
                        </span>
                      )}

                      {/* Agents Badge */}
                      {job.filters?.userIds?.length > 0 && (
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] rounded border border-green-100">
                          {job.filters.userIds.length} Agents
                        </span>
                      )}

                      {/* Wrap-up Badge */}
                      {job.filters?.wrapUpCodes?.length > 0 && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-[10px] rounded border border-purple-100">
                          {job.filters.wrapUpCodes.length} Wrap-ups
                        </span>
                      )}

                      {/* Generic Filter Info if no specific IDs are mapped */}
                      {!job.filters || Object.keys(job.filters).length === 0 ? (
                        <span className="text-xs text-gray-400 italic">No filters applied</span>
                      ) : null}
                    </div>
                  </td>

                  {/* COLUMN 5: ACTIONS */}
                  <td className="px-4 py-4 align-top text-right">
                    <button 
                      onClick={() => handleEditClick(job)} 
                      className="p-2 text-gray-400 hover:text-cyan-600 transition-colors"
                      title="Edit Filters & Schedule"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleDeleteJob(job.jobId)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete Schedule"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card space-y-6">
        <div className="border-b pb-4">
          <h2 className="text-lg font-semibold text-gray-900">
                {editingJobId ? `Edit Schedule: ${jobName}` : 'Create New Schedule'}
          </h2>
          <p className="text-sm text-gray-500">Follow the steps below to configure a safe and efficient automated job.</p>
          {editingJobId && (
              <button onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700 underline">
                Cancel Editing / Create New
              </button>
            )}
        </div>

        {/* STEP 1: Basic Config */}
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center">
            <span className="bg-gray-800 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs mr-2">1</span> 
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-gray-50 p-4 rounded-md border border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Name</label>
              <input type="text" value={jobName} onChange={(e) => setJobName(e.target.value)} placeholder="e.g. Daily MTN Sync" className="w-full px-4 py-2 border rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Campaign</label>
              <select value={selectedCampaign?.value || ''} onChange={(e) => setSelectedCampaign(campaigns.find(c => c.value === e.target.value))} className="w-full px-4 py-2 border rounded-md">
                <option value="" disabled>Select a campaign</option>
                {campaigns.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Scorecard</label>
              <select value={selectedScorecard?.value || ''} onChange={(e) => setSelectedScorecard(scorecards.find(s => s.value === e.target.value))} className="w-full px-4 py-2 border rounded-md" disabled={!selectedCampaign}>
                <option value="" disabled>Select a scorecard</option>
                {scorecards.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* STEP 2: Run Frequency */}
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center">
            <span className="bg-gray-800 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs mr-2">2</span> 
            Run Frequency (SAST)
          </h3>
          <div className="bg-gray-50 p-6 rounded-md border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Repeat On</label>
                <select value={selectedDayOfWeek} onChange={(e) => setSelectedDayOfWeek(e.target.value)} className="w-full p-2 border rounded text-sm focus:ring-cyan-500">
                  <option value="?">Every Day</option>
                  <option value="MON">Mondays</option>
                  <option value="TUE">Tuesdays</option>
                  <option value="WED">Wednesdays</option>
                  <option value="THU">Thursdays</option>
                  <option value="FRI">Fridays</option>
                  <option value="SAT">Saturdays</option>
                  <option value="SUN">Sundays</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Hour (SAST)</label>
                <select value={selectedHour} onChange={(e) => setSelectedHour(e.target.value)} className="w-full p-2 border rounded text-sm focus:ring-cyan-500">
                  {[...Array(24).keys()].map(h => <option key={h} value={h}>{h.toString().padStart(2, '0')}:00</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Minute</label>
                <select value={selectedMinute} onChange={(e) => setSelectedMinute(e.target.value)} className="w-full p-2 border rounded text-sm focus:ring-cyan-500">
                  {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* STEP 3: Relative Time Window */}
        <div>
          <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center">
            <span className="bg-gray-800 text-white w-6 h-6 rounded-full inline-flex items-center justify-center text-xs mr-2">3</span> 
            Data Extraction Window
          </h3>
          <div className={`p-5 rounded-md border space-y-4 transition-colors duration-300 ${hasOverlapRisk ? 'bg-orange-50 border-orange-200' : isInvalidRange ? 'bg-red-50 border-red-200' : 'bg-cyan-50 border-cyan-200'}`}>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Scan From ({getDynamicDateLabel(startDaysAgo)})
                </label>
                <div className="relative">
                  <input type="number" min="1" value={startDaysAgo} onChange={(e) => setStartDaysAgo(e.target.value)} className="w-full pl-3 pr-10 py-2 border rounded text-sm" />
                  <span className="absolute right-3 top-2 text-xs text-gray-400">days ago</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Scan Until ({getDynamicDateLabel(endDaysAgo)})
                </label>
                <div className="relative">
                  <input type="number" min="0" value={endDaysAgo} onChange={(e) => setEndDaysAgo(e.target.value)} className="w-full pl-3 pr-10 py-2 border rounded text-sm" />
                  <span className="absolute right-3 top-2 text-xs text-gray-400">days ago</span>
                </div>
              </div>
            </div>

            {/* DYNAMIC GUARDRAIL WARNINGS */}
            <div className="pt-3 border-t border-gray-200 mt-2">
              {isInvalidRange ? (
                <p className="text-sm text-red-600 font-bold flex items-center">
                  ⚠️ Invalid Range: "Scan From" must be further back in time than "Scan Until".
                </p>
              ) : hasOverlapRisk ? (
                <p className="text-sm text-orange-700 font-medium flex items-start">
                  <span className="mr-2">⚠️</span> 
                  <span>
                    <strong>Overlap Warning:</strong> This job runs every <strong>{frequencyInDays} day(s)</strong>, but extracts <strong>{windowSize} days</strong> of data. This will result in {windowSize - frequencyInDays} day(s) of duplicate data being processed every time it runs.
                  </span>
                </p>
              ) : hasGapRisk ? (
                  <p className="text-sm text-blue-700 font-medium flex items-start">
                    <span className="mr-2">ℹ️</span> 
                    <span>
                      <strong>Data Sampling Mode:</strong> This job runs every <strong>{frequencyInDays} day(s)</strong>, but extracts <strong>{windowSize} day(s)</strong> of data. This means {frequencyInDays - windowSize} day(s) of data between runs will be skipped.
                    </span>
                  </p>
                ) : (
                <p className="text-sm text-green-700 font-bold flex items-center">
                  ✅ Perfect Match: Your {windowSize}-day extraction window perfectly aligns with your {frequencyInDays}-day run frequency.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Fetch Filters (Optional)</h3>
            <button onClick={clearAllFilters} className="text-xs text-cyan-600 hover:text-cyan-700 font-medium">Clear All Filters</button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Call Direction</label>
              <select value={filters.direction} onChange={(e) => updateFilter('direction', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-cyan-500">
                <option value="">All Directions</option>
                <option value="inbound">Inbound Only</option>
                <option value="outbound">Outbound Only</option>
              </select>
            </div>

            <div className="relative">
              <label className="text-xs font-semibold text-gray-500 uppercase">Genesys Queues</label>
              <input type="text" className="w-full p-2 border rounded text-sm focus:ring-cyan-500 outline-none" placeholder={fetchingFilters ? "Loading..." : "Search Queues..."} value={queueSearch} onChange={(e) => { setQueueSearch(e.target.value); setIsQueueOpen(true); }} onFocus={() => setIsQueueOpen(true)} />
              {isQueueOpen && queueSearch && (
                <div className="absolute z-50 w-full bg-white border mt-1 rounded shadow-xl max-h-40 overflow-y-auto">
                  {queueOptions.filter(q => q.name.toLowerCase().includes(queueSearch.toLowerCase())).map(queue => (
                    <div key={queue.id} className="p-2 hover:bg-cyan-50 cursor-pointer text-sm" onClick={() => { if (!filters.queueIds.includes(queue.id)) updateFilter('queueIds', [...filters.queueIds, queue.id]); setQueueSearch(''); setIsQueueOpen(false); }}>{queue.name}</div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {filters.queueIds.map((id, i) => (
                  <span key={id} className="bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full text-xs flex items-center">
                    {queueOptions.find(q => q.id === id)?.name || id}
                    <button onClick={() => removeFromArrayFilter('queueIds', i)} className="ml-1 font-bold">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              <label className="text-xs font-semibold text-gray-500 uppercase">Agents</label>
              <input type="text" className="w-full p-2 border rounded text-sm focus:ring-green-500 outline-none" placeholder="Search Agents..." value={agentSearch} onChange={(e) => { setAgentSearch(e.target.value); setIsAgentOpen(true); }} onFocus={() => setIsAgentOpen(true)} />
              {isAgentOpen && agentSearch && (
                <div className="absolute z-50 w-full bg-white border mt-1 rounded shadow-xl max-h-40 overflow-y-auto">
                  {agentOptions.filter(a => a.name.toLowerCase().includes(agentSearch.toLowerCase())).map(agent => (
                    <div key={agent.id} className="p-2 hover:bg-green-50 cursor-pointer text-sm" onClick={() => { if (!filters.userIds.includes(agent.id)) updateFilter('userIds', [...filters.userIds, agent.id]); setAgentSearch(''); setIsAgentOpen(false); }}>{agent.name}</div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {filters.userIds.map((id, i) => (
                  <span key={id} className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs flex items-center">
                    {agentOptions.find(a => a.id === id)?.name || id}
                    <button onClick={() => removeFromArrayFilter('userIds', i)} className="ml-1 font-bold">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="relative">
              <label className="text-xs font-semibold text-gray-500 uppercase">Wrap-Up Codes</label>
              <input type="text" className="w-full p-2 border rounded text-sm focus:ring-purple-500 outline-none" placeholder="Search Codes..." value={wrapupSearch} onChange={(e) => { setWrapupSearch(e.target.value); setIsWrapupOpen(true); }} onFocus={() => setIsWrapupOpen(true)} />
              {isWrapupOpen && wrapupSearch && (
                <div className="absolute z-50 w-full bg-white border mt-1 rounded shadow-xl max-h-40 overflow-y-auto">
                  {wrapUpCodeOptions.filter(c => c.name.toLowerCase().includes(wrapupSearch.toLowerCase())).map(code => (
                    <div key={code.id} className="p-2 hover:bg-purple-50 cursor-pointer text-sm" onClick={() => { if (!filters.wrapUpCodes.includes(code.id)) updateFilter('wrapUpCodes', [...filters.wrapUpCodes, code.id]); setWrapupSearch(''); setIsWrapupOpen(false); }}>{code.name}</div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {filters.wrapUpCodes.map((id, i) => (
                  <span key={id} className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs flex items-center">
                    {wrapUpCodeOptions.find(c => c.id === id)?.name || id}
                    <button onClick={() => removeFromArrayFilter('wrapUpCodes', i)} className="ml-1 font-bold">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ANI (Caller Number)</label>
                <input type="text" value={filters.ani} onChange={(e) => updateFilter('ani', e.target.value)} placeholder="tel:+1234567890" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-cyan-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">DNIS (Dialed Number)</label>
                <input type="text" value={filters.dnis} onChange={(e) => updateFilter('dnis', e.target.value)} placeholder="tel:+0987654321" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-cyan-500" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Division IDs</label>
              <div className="flex gap-2">
                <input type="text" value={divisionIdInput} onChange={(e) => setDivisionIdInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)} placeholder="Enter division ID and press Enter" className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-cyan-500" />
                <button onClick={() => addToArrayFilter('divisionIds', divisionIdInput, setDivisionIdInput)} className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700">Add</button>
              </div>
              {filters.divisionIds.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {filters.divisionIds.map((id, index) => (
                    <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      {id}
                      <button onClick={() => removeFromArrayFilter('divisionIds', index)} className="ml-2 text-orange-600 hover:text-orange-800">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="pt-4 border-t">
          <button
            onClick={handleCreateSchedule}
            disabled={creatingJob || !selectedCampaign || !selectedScorecard || !jobName}
            className={`w-full py-3 rounded-md font-bold text-white transition-colors ${editingJobId ? 'bg-orange-600 hover:bg-orange-700' : 'bg-cyan-600 hover:bg-cyan-700'} disabled:opacity-50`}
          >
            {creatingJob ? 'Saving...' : editingJobId ? 'Update Scheduled Job' : 'Create Scheduled Job'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduledJobs;